<template>
  <v-form
    ref="formRef"
    class="dry-pump-form method-form"
    @submit.prevent=""
  >
    <!-- Basisdaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-counter</v-icon>
        <span>Basisdaten</span>
      </div>

      <!-- Anzahl -->
      <v-text-field
        v-model.number="form.amount"
        type="number"
        :error-messages="validationErrors?.amount"
        label="Anzahl"
        :hint="hint.amount"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.amount" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.amount }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.amount"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Anzahl übernehmen"
            @click="copyFromReference('amount')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Pumpendaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-pump</v-icon>
        <span>Pumpendaten</span>
      </div>

      <!-- Nennleistung -->
      <v-autocomplete
        v-model="form.ratedPower"
        :error-messages="validationErrors?.rated_power"
        label="Nennleistung"
        :items="ratedPowers"
        item-title="power"
        item-value="id"
        suffix="kW"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.rated_power" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.rated_power }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.rated_power"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Nennleistung übernehmen"
            @click="copyFromReference('rated_power')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Baujahr -->
      <v-text-field
        v-model.number="form.yearBuilt"
        type="number"
        :error-messages="validationErrors?.year_built"
        label="Baujahr"
        :hint="hint.year_built"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.year_built" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.year_built }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.year_built"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Baujahr übernehmen"
            @click="copyFromReference('year_built')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Ausstattung und Betrieb -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-cog-clockwise</v-icon>
        <span>Ausstattung und Betrieb</span>
      </div>

      <!-- VSD / FU -->
      <v-checkbox
        v-model="form.vsdFu"
        :error-messages="validationErrors?.vsd_fu"
        label="Frequenzumrichter (VSD / FU)"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.vsd_fu" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.vsd_fu }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.vsd_fu"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Frequenzumrichter übernehmen"
            @click="copyFromReference('vsd_fu')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>

      <!-- Jährliche Betriebsstunden -->
      <v-text-field
        v-model.number="form.uptime"
        type="number"
        :error-messages="validationErrors?.uptime"
        label="Jährliche Betriebsstunden"
        suffix="h/a"
        :hint="hint.uptime"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.uptime"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Betriebsstunden übernehmen"
            @click="copyFromReference('uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>
  </v-form>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import api from '@/plugins/axios'
import { useBaseForm } from '@/composables/useBaseForm'
// import { useProjectStore } from '@/stores/projectStore'
// import { storeToRefs } from 'pinia'

/* Props match parent (action.vue) */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference:  { type: Boolean, default: false },
  planned:    { type: Boolean, default: false },
  otherData:  { type: Object,  default: () => ({}) },
  sharedData: { type: Object,  default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

/* Pinia (for generator loading state) */
// const projectStore = useProjectStore()
// const { currentProjectActions: actionList } = storeToRefs(projectStore)

/* Field mapping (frontend -> backend) */
const fields = {
  actionId:      'action_id',
  amount:        'amount',
  ratedPower:    'rated_power',
  yearBuilt:     'year_built',
  vsdFu:         'vsd_fu',
  uptime:        'uptime',
}

/* useBaseForm integration */
const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints,
} = useBaseForm({
  value:       computed(() => props.modelValue),
  fields,
  defaults:    { vsdFu: false },
  reference:   computed(() => props.reference),
  planned:     computed(() => props.planned),
  shared:      computed(() => false), // not a shared form
  otherData:   computed(() => props.otherData),
  sharedData:  computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit:        (event, payload) => emit(event, payload),
})

/* Local lists */
const ratedPowers = ref([])

/* Fetchers (updated endpoints, keep cache adapter) */
async function fetchRatedPowers() {
  try {
    const { data } = await api.get('/methods/drypump1/rated-powers/')
    ratedPowers.value = data // [{ id, power }, ...]
  } catch {
    // optionally: toast or error banner
  }
}

/* Expose validate for parent (error highlighting on failed save) */
const formRef = ref(null)
function validateForm() {
  return formRef.value?.validate?.()
}

defineExpose({
  validateForm,
  fields: Object.values(fields),
})


/* Lifecycle */
onMounted(async () => {
  await fetchRatedPowers()
  await fetchHints()
})
</script>

<style scoped lang="scss">
.dry-pump-form {
  width: 100%;
}

/* Input fields */
.dry-pump-form :deep(.v-field) {
  border-radius: 18px;
}

.dry-pump-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Visual sections */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

.method-section:last-child {
  margin-bottom: 0;
}

/* Section headings */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);
  font-size: 1rem;
  font-weight: 700;
}

/* Checkbox */
.dry-pump-form :deep(.v-checkbox) {
  padding: 0.35rem 0.65rem;
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Help and reference-copy actions */
.dry-pump-form :deep(.v-input__append),
.dry-pump-form :deep(.v-field__append-inner) {
  align-items: center;
}

.dry-pump-form :deep(.v-input__append .v-icon),
.dry-pump-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.dry-pump-form :deep(.v-input__append .v-btn),
.dry-pump-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.dry-pump-form :deep(.v-input__append .v-btn:hover),
.dry-pump-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Disabled states */
.dry-pump-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }
}
</style>
